/**
 * EFormFieldProperties — the Form Builder's right panel, drawn as corp's Field
 * Properties (corporate-dashboard components/form-builder.tsx), for the chosen
 * question, by its type:
 *   Field Type (+ Required, or Address's required parts); Visibility; Page
 *   Title (page break); Question Text / Content and Instruction Text (rich
 *   text); Merchant (credit card); Answer Options (choice types); Question
 *   Group; Populates (the record field an answer fills in) + Show in Summary;
 *   Logic Rules.
 * The panel edits the question through `onChange`; nothing is saved until
 * Save All, except what corp also writes at once: a new option group, and
 * "Save to Group" for a group's edited options. Options still edited at Save
 * All are saved then too (corp dropped them).
 *
 * Inside a locked FieldLockScope (a Locked E-Forms page, plan 2026-10-10-001,
 * U7) the panel shows the question's properties to read: the shared inputs
 * take no change, the native selects show their choice as text, and every
 * edit button (pencils, New, Add Option, Add Condition, move, remove, Save to
 * Group, Require all) is absent. Nothing says so (R18).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronRight, Loader2, Pencil, Plus, Save, Settings, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  ADDRESS,
  ADDRESS_PARTS,
  CHOICE_TYPES,
  CONTENT_TYPES,
  CREDIT_CARD,
  NOT_LOGIC_TARGETS,
  PAGE_BREAK,
  QUESTION_GROUPS,
  QUESTION_TYPES,
  SharedOptionGroupError,
  createOptionGroup,
  fieldOptions,
  listOptionGroups,
  plainText,
  questionTypeLabel,
  updateOptionGroup,
  type BuilderField,
  type DemographicField,
  type LogicRule,
  type OptionGroup,
  type QuestionGroup,
  type QuestionOption,
} from '@/services/eformsAdmin.service';
import type { Merchant } from '@/services/payments.service';
import { toast } from '@/store/toastStore';
import { OutlineBadge } from './CorpBadges';
import { RichTextDialog } from './RichTextDialog';
import { QuestionTypeIcon } from './EFormQuestionTypeIcon';

/**
 * The account's HTML as corp's `prose` draws it (Prospera has no typography
 * plugin): links, lists, headings and images. RICH_TEXT_BLOCK adds the
 * paragraph spacing, for content shown in full.
 */
export const RICH_TEXT =
  '[&_a]:text-blue-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-base [&_h3]:font-semibold [&_img]:h-auto [&_img]:max-w-full';
export const RICH_TEXT_BLOCK = `${RICH_TEXT} [&_p]:my-3 [&_ul]:my-3 [&_ol]:my-3 [&_h1]:mb-3 [&_h2]:mb-2 [&_h2]:mt-4 [&_h3]:mb-1 [&_h3]:mt-3 [&>:first-child]:mt-0 [&>:last-child]:mb-0`;

const LABEL = 'block text-xs font-semibold uppercase tracking-wide text-neutral-500';
const LOGIC_SELECT = 'h-6 rounded border border-neutral-200 bg-transparent px-1 text-xs';
const SMALL = 'h-7 py-0.5 text-xs';

/** A native select's choice as plain text, for a locked panel. */
function LockedChoice({ label, text, className }: { label: string; text: string; className?: string }) {
  return (
    <span aria-label={label} className={cn('inline-flex items-center', className)}>
      {text}
    </span>
  );
}

/** A section's small uppercase heading; the label of its control when `htmlFor` is given. */
function SectionLabel({ children, className, htmlFor }: { children: ReactNode; className?: string; htmlFor?: string }) {
  return htmlFor ? (
    <label htmlFor={htmlFor} className={cn(LABEL, className)}>
      {children}
    </label>
  ) : (
    <p className={cn(LABEL, className)}>{children}</p>
  );
}

function SwitchRow({
  id,
  label,
  checked,
  onChange,
  labelClassName = 'text-sm',
  className,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  labelClassName?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <label htmlFor={id} className={cn('cursor-pointer font-medium leading-none', labelClassName)}>
        {label}
      </label>
      <Switch id={id} checked={checked} onChange={onChange} aria-label={label} />
    </div>
  );
}

/** A question's short name for the logic pickers: its first 30 characters, or Q{n}. */
function questionShortLabel(field: BuilderField, index: number): string {
  return plainText(field.content).slice(0, 30) || `Q${index + 1}`;
}

/** A page break's title as corp edits it: tags dropped, and none for legacy "Page Break". */
function pageTitle(content: string): string {
  const raw = content.replace(/<[^>]*>/g, '');
  return raw.trim().toLowerCase() === 'page break' ? '' : raw;
}

/** Corp's rich-text box: the HTML, and a pencil on hover that opens the editor. */
function RichTextPreview({
  html,
  empty,
  editLabel,
  dialogTitle,
  sizeClass,
  onChange,
}: {
  html: string;
  empty: string;
  editLabel: string;
  dialogTitle: string;
  sizeClass: string;
  onChange: (html: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const locked = useFieldLocked();
  const box = cn('overflow-y-auto rounded border border-neutral-200 bg-neutral-100/30 p-2 pr-9 text-sm', sizeClass);
  return (
    <div className="group/rtp relative">
      {html ? (
        <div className={cn(box, RICH_TEXT_BLOCK)} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <div className={box}>
          <em className="text-gray-400">{empty}</em>
        </div>
      )}
      {!locked && (
        <button
          type="button"
          title={editLabel}
          aria-label={editLabel}
          onClick={() => setOpen(true)}
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 opacity-0 transition-all hover:bg-black/70 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 group-hover/rtp:opacity-100"
        >
          <Pencil className="h-3 w-3 text-white" aria-hidden />
        </button>
      )}
      {open && !locked && <RichTextDialog title={dialogTitle} value={html} onApply={onChange} onClose={() => setOpen(false)} />}
    </div>
  );
}

export interface EFormFieldPropertiesProps {
  field: BuilderField | null;
  position: number;
  fields: BuilderField[];
  groups: QuestionGroup[];
  optionGroups: OptionGroup[];
  demographics: DemographicField[];
  merchants: Merchant[];
  onChange: (next: BuilderField) => void;
  /** The option groups after one was created or saved from the options editor. */
  onOptionGroupsChange: (groups: OptionGroup[]) => void;
}

/** Field Type's list: corp's four sections, each under its heading. */
const TYPE_OPTIONS = QUESTION_GROUPS.flatMap((g, gi) => [
  { value: -(gi + 1), label: g, disabled: true },
  ...QUESTION_TYPES.filter((q) => q.group === g).map((q) => ({ value: q.id, label: q.label })),
]);

function typeOptionLabel(o: { value: number; label: string }) {
  // A heading (disabled, so the list dims it to corp's muted grey).
  if (o.value < 0) return <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-900">{o.label}</span>;
  return (
    <span className="flex items-center gap-2">
      <QuestionTypeIcon type={o.value} className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
      {o.label}
    </span>
  );
}

export function EFormFieldProperties({
  field,
  position,
  fields,
  groups,
  optionGroups,
  demographics,
  merchants,
  onChange,
  onOptionGroupsChange,
}: EFormFieldPropertiesProps) {
  const sortedDemographics = useMemo(() => [...demographics].sort((a, b) => a.label.localeCompare(b.label)), [demographics]);
  const locked = useFieldLocked();

  if (!field) {
    return (
      <div className="flex shrink-0 flex-col items-center justify-center border-t border-neutral-200 bg-gray-50/50 p-6 text-center lg:w-[320px] lg:border-l lg:border-t-0">
        <Settings className="mb-2 h-8 w-8 text-gray-300" aria-hidden />
        <p className="font-medium text-gray-400">Field Properties</p>
        <p className="mt-1 text-xs text-neutral-500">
          {locked ? 'Select a field on the canvas to see its properties' : 'Select a field on the canvas to edit its properties'}
        </p>
      </div>
    );
  }

  const f = field;
  const set = (patch: Partial<BuilderField>) => {
    if (!locked) onChange({ ...f, ...patch });
  };
  const isPageBreak = f.type === PAGE_BREAK;
  const isContent = CONTENT_TYPES.includes(f.type);
  const showRequired = f.type !== 15 && !isPageBreak && f.type !== 27 && f.type !== ADDRESS;
  // A locked panel shows no "required" warnings: nothing there can be fixed.
  const merchantMissing = !locked && !f.instruction.trim();

  function changeType(type: number) {
    if (type === f.type) return;
    const text = plainText(f.content);
    // As corp: a question still reading as its type follows the new type.
    const relabel = text === '' || QUESTION_TYPES.some((q) => q.label === text);
    set({
      type,
      content: relabel ? (type === PAGE_BREAK ? '' : questionTypeLabel(type)) : f.content,
      checkAll: type === 8 ? f.checkAll : false,
    });
  }

  return (
    <div className="flex shrink-0 flex-col border-t border-neutral-200 bg-white lg:w-[320px] lg:overflow-hidden lg:border-l lg:border-t-0">
      <div className="flex shrink-0 items-center gap-2 border-b border-neutral-200 px-4 py-2.5">
        <QuestionTypeIcon type={f.type} className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-semibold">{questionTypeLabel(f.type)}</span>
        <OutlineBadge className="ml-auto text-[10px]">#{position}</OutlineBadge>
      </div>

      <div className="flex-1 space-y-5 p-4 lg:overflow-y-auto">
        {/* ── Field Type ── */}
        <div className="space-y-1.5">
          <SectionLabel htmlFor="eform-field-type">Field Type</SectionLabel>
          <Select<number>
            id="eform-field-type"
            options={TYPE_OPTIONS}
            value={f.type}
            onChange={(v) => v != null && v > 0 && changeType(v)}
            className="h-8 py-1 text-sm"
            renderOption={typeOptionLabel}
            renderValue={typeOptionLabel}
          />
          {showRequired && <SwitchRow id="eform-field-required" label="Required" checked={f.required} onChange={(v) => set({ required: v })} className="pt-1" />}
          {f.type === ADDRESS && (
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium leading-none">Required Fields</span>
                {!locked && (
                  <button
                    type="button"
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
                    onClick={() => set({ addressMask: f.addressMask === 15 ? 0 : 15 })}
                  >
                    {f.addressMask === 15 ? 'Uncheck all' : 'Require all'}
                  </button>
                )}
              </div>
              {ADDRESS_PARTS.map((p) => (
                <SwitchRow
                  key={p.bit}
                  id={`eform-address-${p.bit}`}
                  label={p.label}
                  labelClassName="text-xs text-neutral-500"
                  className="pl-1"
                  checked={(f.addressMask & p.bit) !== 0}
                  onChange={(v) => set({ addressMask: v ? f.addressMask | p.bit : f.addressMask & ~p.bit })}
                />
              ))}
              <p className="text-[11px] italic text-neutral-500">Address Line 2 is always optional</p>
            </div>
          )}
        </div>

        {/* ── Visibility ── */}
        {!isPageBreak && (
          <div className="space-y-1.5">
            <SectionLabel htmlFor="eform-field-visibility">Visibility</SectionLabel>
            <Select<number>
              id="eform-field-visibility"
              options={[
                { value: 0, label: 'Everyone' },
                { value: 1, label: 'Patients Only' },
                { value: 2, label: 'Non-Patients Only' },
              ]}
              value={f.showTo}
              onChange={(v) => set({ showTo: v ?? 0 })}
              className="h-8 py-1 text-sm"
            />
          </div>
        )}

        {/* ── Page Title (page breaks) ── */}
        {isPageBreak && (
          <div className="space-y-1.5">
            <SectionLabel>Page Title</SectionLabel>
            <Input
              aria-label="Page title"
              value={pageTitle(f.content)}
              placeholder="Optional title for this page section"
              className="h-9 py-1 text-sm"
              onChange={(e) => set({ content: e.target.value })}
            />
            <p className="text-xs text-neutral-500">Shown at the top of this page section on the eform.</p>
          </div>
        )}

        {!isPageBreak && (
          <>
            {/* ── Question Text ── */}
            <div className="space-y-1.5">
              <SectionLabel>{isContent ? 'Content' : 'Question Text'}</SectionLabel>
              <RichTextPreview
                html={f.content}
                empty={isContent ? 'No content' : 'No question text'}
                editLabel={isContent ? 'Edit content' : 'Edit question text'}
                dialogTitle={isContent ? 'Edit Content' : 'Edit Question Text'}
                sizeClass="min-h-[40px] max-h-[150px]"
                onChange={(html) => set({ content: html })}
              />
            </div>

            {/* ── Instruction Text (not hidden fields or card entry) ── */}
            {f.type !== 15 && f.type !== CREDIT_CARD && (
              <div className="space-y-1.5">
                <SectionLabel>Instruction Text</SectionLabel>
                <RichTextPreview
                  html={f.instruction}
                  empty="No instruction text"
                  editLabel="Edit instruction text"
                  dialogTitle="Edit Instruction Text"
                  sizeClass="min-h-[32px] max-h-[120px]"
                  onChange={(html) => set({ instruction: html })}
                />
              </div>
            )}

            {/* ── Merchant (card entry keeps the merchant id in the instruction) ── */}
            {f.type === CREDIT_CARD && (
              <div className={cn('space-y-1.5', merchantMissing && 'rounded-md border border-red-300 bg-red-50/50 p-2')}>
                <SectionLabel htmlFor="eform-field-merchant" className={cn(merchantMissing && 'text-red-700')}>
                  Merchant {merchantMissing && <span className="font-normal text-red-500">— required</span>}
                </SectionLabel>
                {locked ? (
                  <LockedChoice
                    label="Merchant"
                    text={
                      f.instruction.trim()
                        ? merchants.find((m) => String(m.merchantId) === f.instruction)?.friendlyTitle || f.instruction
                        : 'Not Specified'
                    }
                    className="h-9 w-full rounded-md border border-neutral-200 px-3 text-sm"
                  />
                ) : (
                  <select
                    id="eform-field-merchant"
                    value={f.instruction}
                    onChange={(e) => set({ instruction: e.target.value })}
                    className={cn('h-9 w-full rounded-md border bg-transparent px-3 text-sm', merchantMissing ? 'border-red-400 ring-1 ring-red-200' : 'border-neutral-200')}
                  >
                    <option value="">Not Specified</option>
                    {merchants.map((m) => (
                      <option key={m.merchantId} value={String(m.merchantId)}>
                        {m.friendlyTitle}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* ── Answer Options (choice types) ── */}
            {CHOICE_TYPES.includes(f.type) && (
              <div className="border-t border-neutral-200 pt-4">
                <OptionsEditor field={f} optionGroups={optionGroups} onChange={onChange} onOptionGroupsChange={onOptionGroupsChange} />
              </div>
            )}

            {/* ── Question Group ── */}
            <div className="space-y-1.5 border-t border-neutral-200 pt-4">
              <SectionLabel htmlFor="eform-field-group">Question Group</SectionLabel>
              <Select<number>
                id="eform-field-group"
                options={[{ value: 0, label: 'No Group' }, ...groups.map((g) => ({ value: g.id, label: g.name || `Group #${g.id}` }))]}
                value={f.groupId}
                onChange={(v) => set({ groupId: v ?? 0 })}
                placeholder="No group"
                className="h-8 py-1 text-sm"
              />
            </div>

            {/* ── Populates (Email/SMS Opt-In maps itself) ── */}
            {f.type !== 27 && (
              <div className="space-y-1.5">
                <SectionLabel htmlFor="eform-field-populates">Populates (Record Field)</SectionLabel>
                <SearchableCombobox<string>
                  id="eform-field-populates"
                  options={[
                    { value: '', label: 'None' },
                    ...sortedDemographics.map((d) => ({ value: d.key, label: d.label, keywords: d.key })),
                    ...(f.populates && !demographics.some((d) => d.key === f.populates) ? [{ value: f.populates, label: f.populates }] : []),
                  ]}
                  value={f.populates}
                  onChange={(v) => set({ populates: v ?? '' })}
                  placeholder="Search record fields..."
                  className="h-8 py-1 text-sm"
                />
                <SwitchRow id="eform-field-summary" label="Show in Summary" checked={f.summary} onChange={(v) => set({ summary: v })} className="pt-1" />
              </div>
            )}

            {/* ── Logic Rules (not hidden fields) ── */}
            {f.type !== 15 && <LogicPanel field={f} fields={fields} optionGroups={optionGroups} onChange={onChange} />}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Answer options ────────────────────────────────────────────────────────────

/** Groups worth offering: with options, and one per identical option list (the current one always). */
function offeredGroups(optionGroups: OptionGroup[], currentId: number): OptionGroup[] {
  const seen = new Set<string>();
  return [...optionGroups]
    .sort((a, b) => a.title.localeCompare(b.title))
    .filter((g) => {
      if (g.id === currentId) return true;
      if (g.options.length === 0) return false;
      const sig = g.options
        .map((o) => o.text.trim().toLowerCase())
        .sort()
        .join('\u0000');
      if (seen.has(sig)) return false;
      seen.add(sig);
      return true;
    });
}

function AmberNote({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/50 p-2">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
      <span className="flex-1 text-xs text-amber-700">{children}</span>
      {action}
    </div>
  );
}

function OptionsEditor({
  field,
  optionGroups,
  onChange,
  onOptionGroupsChange,
}: {
  field: BuilderField;
  optionGroups: OptionGroup[];
  onChange: (next: BuilderField) => void;
  onOptionGroupsChange: (groups: OptionGroup[]) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [savingToGroup, setSavingToGroup] = useState(false);
  const locked = useFieldLocked();
  const options = fieldOptions(field, optionGroups);
  const hasGroup = field.optionGroupId > 0;
  const groupTitle = optionGroups.find((g) => g.id === field.optionGroupId)?.title || `Option Group ${field.optionGroupId}`;
  const groups = useMemo(() => offeredGroups(optionGroups, field.optionGroupId), [optionGroups, field.optionGroupId]);
  // The same rule as the errors badge: no option with text.
  const missing = !locked && options.every((o) => !o.text.trim());

  const edit = (next: QuestionOption[]) => {
    if (!locked) onChange({ ...field, options: next });
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    edit(options.map((x, k) => (k === i ? options[j] : k === j ? options[i] : x)));
  };

  async function handleCreate() {
    const title = newName.trim();
    if (!title || creating) return;
    setCreating(true);
    try {
      // The new group starts with the question's options, so it keeps them.
      const kept = options.filter((o) => o.text.trim());
      const id = await createOptionGroup(title, kept);
      onOptionGroupsChange([...optionGroups, { id, title, options: kept.map((o) => ({ id: null, text: o.text })) }]);
      onChange({ ...field, optionGroupId: id, options: null });
      toast.success(`Created option group "${title}"`);
      setShowCreate(false);
      setNewName('');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create option group'));
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveToGroup() {
    if (!hasGroup || !field.options || savingToGroup) return;
    setSavingToGroup(true);
    try {
      await updateOptionGroup(field.optionGroupId, groupTitle, field.options);
      const saved = field.options.filter((o) => o.text.trim());
      // Reloaded, the new options carry their ids (logic can then test them).
      const fresh = await listOptionGroups().catch(() => null);
      onOptionGroupsChange(fresh ?? optionGroups.map((g) => (g.id === field.optionGroupId ? { ...g, options: saved } : g)));
      onChange({ ...field, options: null });
      toast.success('Options saved to group');
    } catch (err) {
      if (err instanceof SharedOptionGroupError) toast.error(`Shared option group — can't edit here. ${err.message}`);
      else toast.error(getErrorMessage(err, 'Failed to save options to group.'));
    } finally {
      setSavingToGroup(false);
    }
  }

  return (
    <div className={cn('space-y-3', missing && 'rounded-md border border-red-300 bg-red-50/50 p-2')}>
      <SectionLabel htmlFor="eform-option-group" className={cn(missing && 'text-red-700')}>
        Answer Options {missing && <span className="font-normal text-red-500">— required</span>}
      </SectionLabel>

      <div className="flex items-center gap-1.5">
        <SearchableCombobox<number>
          id="eform-option-group"
          wrapperClassName="min-w-0 flex-1"
          className={SMALL}
          options={[{ value: 0, label: '-- No Group --' }, ...groups.map((g) => ({ value: g.id, label: g.title || `Option Group ${g.id}` }))]}
          value={field.optionGroupId}
          onChange={(v) => onChange({ ...field, optionGroupId: v ?? 0, options: null })}
          placeholder="Select option group..."
        />
        {!locked && (
          <Button variant="secondary" size="xs" className="h-7 shrink-0" leftIcon={<Plus className="h-3 w-3" />} onClick={() => setShowCreate((c) => !c)}>
            New
          </Button>
        )}
      </div>

      {showCreate && !locked && (
        <div className="flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50/50 p-2">
          <Input
            aria-label="New group name"
            value={newName}
            placeholder="New group name..."
            autoFocus
            wrapperClassName="min-w-0 flex-1"
            className={SMALL}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleCreate();
              }
            }}
          />
          <Button size="xs" className="h-7" disabled={creating || !newName.trim()} onClick={() => void handleCreate()}>
            {creating ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Creating" /> : 'Create'}
          </Button>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            className="h-7 w-7"
            leftIcon={<X className="h-3 w-3" />}
            aria-label="Cancel new group"
            onClick={() => {
              setShowCreate(false);
              setNewName('');
            }}
          />
        </div>
      )}

      {!locked && hasGroup && field.options && (
        <AmberNote
          action={
            <Button
              size="xs"
              disabled={savingToGroup}
              leftIcon={savingToGroup ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              onClick={() => void handleSaveToGroup()}
            >
              {savingToGroup ? undefined : 'Save to Group'}
            </Button>
          }
        >
          Options modified locally. Save to update the group.
        </AmberNote>
      )}
      {!locked && !hasGroup && field.options && options.length > 0 && (
        <AmberNote>Options need an option group to be saved. Save All creates one named after this question, or select or create a group.</AmberNote>
      )}

      {options.length > 0 ? (
        <div className="space-y-1">
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-1">
              <Input
                aria-label={`Option ${i + 1}`}
                value={o.text}
                placeholder={`Option ${i + 1}`}
                wrapperClassName="min-w-0 flex-1"
                className={SMALL}
                onChange={(e) => edit(options.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
              />
              {!locked && (
                <>
              <button
                type="button"
                aria-label={`Move option ${i + 1} up`}
                disabled={i === 0}
                onClick={() => move(i, -1)}
                className={cn('shrink-0 p-0.5', i === 0 ? 'text-gray-200' : 'text-gray-400 hover:text-gray-700')}
              >
                <ChevronRight className="h-3.5 w-3.5 -rotate-90" aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Move option ${i + 1} down`}
                disabled={i === options.length - 1}
                onClick={() => move(i, 1)}
                className={cn('shrink-0 p-0.5', i === options.length - 1 ? 'text-gray-200' : 'text-gray-400 hover:text-gray-700')}
              >
                <ChevronRight className="h-3.5 w-3.5 rotate-90" aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Remove option ${i + 1}`}
                onClick={() => edit(options.filter((_, j) => j !== i))}
                className="shrink-0 p-0.5 text-gray-400 hover:text-red-500"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className={cn('text-xs italic', locked ? 'text-neutral-500' : 'text-red-600')}>No options added</p>
      )}

      {!locked && (
        <Button variant="secondary" size="xs" className="h-7 w-full" leftIcon={<Plus className="h-3 w-3" />} onClick={() => edit([...options, { id: null, text: '' }])}>
          Add Option
        </Button>
      )}

      <div className="space-y-2.5 border-t border-neutral-200 pt-2">
        {field.type === 8 && (
          <SwitchRow
            id="eform-option-check-all"
            label='Add "Check All"'
            labelClassName="text-xs"
            checked={field.checkAll}
            onChange={(v) => onChange({ ...field, checkAll: v })}
          />
        )}
        <SwitchRow id="eform-option-other" label='Add "Other" option' labelClassName="text-xs" checked={field.addOther} onChange={(v) => onChange({ ...field, addOther: v })} />
      </div>
    </div>
  );
}

// ─── Logic ─────────────────────────────────────────────────────────────────────

/** The logic selects' choices, by their stored value (the index). */
const LOGIC_ACTIONS = ['Do nothing', 'Show', 'Hide'];
const LOGIC_CONDITIONS = ['all', 'any'];
const LOGIC_OPERATORS = ['is', 'is not', '<', '>'];

function LogicPanel({
  field,
  fields,
  optionGroups,
  onChange,
}: {
  field: BuilderField;
  fields: BuilderField[];
  optionGroups: OptionGroup[];
  onChange: (next: BuilderField) => void;
}) {
  // Rules test saved questions only (a new one has no id to point at yet).
  const targets = fields
    .map((q, i) => ({ q, i }))
    .filter(({ q }) => q.key !== field.key && q.orderId !== null && !NOT_LOGIC_TARGETS.includes(q.type));
  const locked = useFieldLocked();
  const setRules = (rules: LogicRule[]) => onChange({ ...field, rules, ...(rules.length === 0 ? { logicAction: 0, logicCondition: 0 } : {}) });
  const setRule = (i: number, patch: Partial<LogicRule>) => setRules(field.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="border-t border-neutral-200 pt-4">
      <div className="mb-2 flex items-center justify-between">
        <SectionLabel>Logic Rules</SectionLabel>
        {!locked && (
        <Button
          variant="secondary"
          size="xs"
          leftIcon={<Plus className="h-3 w-3" />}
          onClick={() =>
            onChange({
              ...field,
              logicAction: field.rules.length === 0 ? 1 : field.logicAction,
              rules: [...field.rules, { id: null, targetOrderId: null, operator: 0, optionId: 0, value: '' }],
            })
          }
        >
          Add Condition
        </Button>
        )}
      </div>
      {field.rules.length === 0 ? (
        <p className="text-xs italic text-neutral-500">No logic rules configured</p>
      ) : (
        <div className="space-y-2 rounded-md border border-neutral-200 bg-gray-50 p-2.5">
          <div className="flex flex-wrap items-center gap-1">
            {locked ? (
              <LockedChoice label="Logic action" text={LOGIC_ACTIONS[field.logicAction] ?? LOGIC_ACTIONS[0]} className={LOGIC_SELECT} />
            ) : (
              <select aria-label="Logic action" className={LOGIC_SELECT} value={field.logicAction} onChange={(e) => onChange({ ...field, logicAction: Number(e.target.value) })}>
                {LOGIC_ACTIONS.map((t, v) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            )}
            <span className="text-xs text-gray-500">this field when</span>
            {locked ? (
              <LockedChoice label="Logic condition" text={LOGIC_CONDITIONS[field.logicCondition] ?? LOGIC_CONDITIONS[0]} className={LOGIC_SELECT} />
            ) : (
              <select aria-label="Logic condition" className={LOGIC_SELECT} value={field.logicCondition} onChange={(e) => onChange({ ...field, logicCondition: Number(e.target.value) })}>
                {LOGIC_CONDITIONS.map((t, v) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            )}
            <span className="text-xs text-gray-500">match:</span>
          </div>
          {field.rules.map((rule, i) => {
            const target = fields.find((q) => q.orderId !== null && q.orderId === rule.targetOrderId) ?? null;
            const answers = target && CHOICE_TYPES.includes(target.type) ? fieldOptions(target, optionGroups).filter((o) => o.id !== null) : [];
            return (
              <div key={i} className="space-y-1 border-t border-neutral-200 pt-2">
                <div className="flex items-center gap-1 text-xs">
                  {locked ? (
                    <>
                      <LockedChoice
                        label={`Rule ${i + 1} question`}
                        text={target ? questionShortLabel(target, fields.indexOf(target)) : 'Question...'}
                        className={cn(LOGIC_SELECT, 'min-w-0 flex-1')}
                      />
                      <LockedChoice
                        label={`Rule ${i + 1} operator`}
                        text={LOGIC_OPERATORS[rule.operator] ?? LOGIC_OPERATORS[0]}
                        className={cn(LOGIC_SELECT, 'w-[70px] shrink-0')}
                      />
                    </>
                  ) : (
                    <>
                  <select
                    aria-label={`Rule ${i + 1} question`}
                    className={cn(LOGIC_SELECT, 'min-w-0 flex-1')}
                    value={rule.targetOrderId ?? ''}
                    onChange={(e) => setRule(i, { targetOrderId: e.target.value ? Number(e.target.value) : null, optionId: 0, value: '' })}
                  >
                    <option value="">Question...</option>
                    {targets.map(({ q, i: qi }) => (
                      <option key={q.key} value={q.orderId ?? ''}>
                        {questionShortLabel(q, qi)}
                      </option>
                    ))}
                  </select>
                  <select aria-label={`Rule ${i + 1} operator`} className={cn(LOGIC_SELECT, 'w-[70px] shrink-0')} value={rule.operator} onChange={(e) => setRule(i, { operator: Number(e.target.value) })}>
                    {LOGIC_OPERATORS.map((t, v) => (
                      <option key={v} value={v}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    aria-label={`Remove rule ${i + 1}`}
                    onClick={() => setRules(field.rules.filter((_, j) => j !== i))}
                    className="shrink-0 p-0.5 text-gray-400 transition-colors hover:text-red-500"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                  </button>
                    </>
                  )}
                </div>
                {locked && answers.length > 0 ? (
                  <LockedChoice
                    label={`Rule ${i + 1} answer`}
                    text={answers.find((o) => o.id === rule.optionId)?.text ?? 'Select an answer...'}
                    className={cn(LOGIC_SELECT, 'w-full')}
                  />
                ) : answers.length > 0 ? (
                  <select
                    aria-label={`Rule ${i + 1} answer`}
                    className={cn(LOGIC_SELECT, 'w-full')}
                    value={rule.optionId || ''}
                    onChange={(e) => setRule(i, { optionId: Number(e.target.value) || 0 })}
                  >
                    <option value="">Select an answer...</option>
                    {answers.map((o) => (
                      <option key={o.id ?? o.text} value={o.id ?? ''}>
                        {o.text}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input aria-label={`Rule ${i + 1} value`} className="h-6 py-0 text-xs" value={rule.value} placeholder="Value..." onChange={(e) => setRule(i, { value: e.target.value })} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
